import React, { Dispatch, FormEvent, SetStateAction, useContext, useEffect, useMemo, useRef, useState } from "react";
import CustomerForm from "./Components/CustomerForm";
import CustomerQuotationRow from "./Components/CustomerQuotationRow";
import { Col, Overlay, Row, Spinner } from "react-bootstrap";
import { checkExistCustomerEnquiry, getCustomerData, getCustomerDataById, getQuotationById, updateCustomer } from "../../../api/customer.api";
import { SelectedUserContext } from "../../../context/selecteduser.context";
import CustomerEnquiry from "./Components/CustomerEnquiry";
import { SearchIconSvg } from "../../../assets/svg";
import '../style.scss'
import { debounce, isEmpty } from "lodash";

interface IBasicDetails {
  setStepNumber: Dispatch<SetStateAction<number>>;
}

const BasicDetails: React.FC<IBasicDetails> = ({ setStepNumber }) => {
  const inputRef = useRef<HTMLInputElement>(null);

  const { handleSelectedUser, selectedUser, handleExistingEnquiry } = useContext(SelectedUserContext);

  const [prevQuotationData, setPrevQuotationData] = useState([]);
  const [customerEnquirydata, setCustomerEnquiryData] = useState({});
  const [searchText, setSearchText] = useState<string>("");
  const [suggestions, setSuggestions] = useState<any>([]);
  const [searchStatus, setSearchStatus] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [resetForm, setResetForm] = useState<number>(0);

  const debouncedSearch = useMemo(() => {
    return debounce(async(value) => {
      if (value) {
        const data = await getCustomerData(value);
        const filteredSuggestions: any = data?.result?.result?.filter(
          (item: any) =>
            (item?.firstName + " " + item?.lastName).toLowerCase().includes(value.toLowerCase()) ||
            item?.email?.toLowerCase().includes(value.toLowerCase()) ||
            item?.phoneNumber?.toLowerCase().includes(value.toLowerCase())
        );
        setLoading(false);
        setSuggestions(filteredSuggestions);
      }
    }, 400);
  }, []);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchStatus(false);
    setSuggestions([]);
    const { value } = e.target;
    setSearchText(value);
  };

  const handleSearchSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    setSearchStatus(true);
    debouncedSearch(searchText);
  }

  const handleRowClick = async (id: string) => {
    const data = await getCustomerDataById(id);
    if (data?.status === 200) {
      setSearchText('');
      setSuggestions([]);
      setSearchStatus(false);
      let userData = data?.result;
      handleSelectedUser(data?.result);
      const existEnquiry = await checkExistCustomerEnquiry(data?.result?.id);
      if (existEnquiry.status === 200) {
        handleExistingEnquiry(existEnquiry?.result);
        setCustomerEnquiryData(existEnquiry?.result);
      }
      const quotation= await getQuotationById(userData?.id)
      if(quotation?.status===200){
      setPrevQuotationData(quotation?.result)
      }
    }
  };

  const handleReset = () => {
    setSearchText("");
    handleSelectedUser({ email: "", firstName: "", lastName: "", countryCode:"", phoneNumber: "", whatsappCountryCode:"", whatsappNumber: "", accountSource: "" });
    handleExistingEnquiry({});
    setCustomerEnquiryData({});
    setPrevQuotationData([]);
    setResetForm((prev) => prev + 1);
  };

  useEffect(() => { 
    if (!!selectedUser?.id) {
      handleRowClick(selectedUser?.id);
    }
  }, [])

  return (
    <div className="mb-3">
      <Row className=" border bg-white rounded w-100 m-0">
        <Col className="px-4 py-4">
          <div>
            <div className="flex justify-between items-center mb-4">
              <span style={{ fontSize: "22px", fontWeight: "700", color: "#3A3A3A" }}>
                Search Existing Customers
              </span>
              <span className="cursor-pointer border-b border-b-[#d0566e] h-fit]"
                onClick={ handleReset }>
                Reset
              </span>
            </div>
            <form className="flex gap-6 items-center mb-3 mx-0" onSubmit={handleSearchSubmit}>
              <div className="flex search-global-cont w-[45vw] pb-1 items-center">
                <SearchIconSvg/>
                <input
                  ref={inputRef}
                  autoComplete="off"
                  className="search-global ml-2 w-100"
                  placeholder="Search by Customer Name, Email , Phone Number"
                  name={"search"}
                  onChange={handleSearchChange}
                  value={searchText}
                />
              </div>
              <button
                type="submit"
                className="btn text-light px-3 mx-3 min-w-[120px]"
                style={{ background: "linear-gradient(305.75deg, #92278F -144.06%, #EC7765 147.18%)" }}
              >
                Search
              </button>
            </form>
            {searchStatus && (
              <Overlay target={inputRef.current} show={searchStatus} placement="bottom" onHide={() => setSearchStatus(false)} rootClose >
                {({
                  placement: _placement,
                  arrowProps: _arrowProps,
                  show: _show,
                  popper: _popper,
                  hasDoneInitialMeasure: _hasDoneInitialMeasure,
                  ...props
                }) => (
                  <div
                    {...props}
                    style={{
                      position: "absolute",
                      backgroundColor: "#ffff",
                      padding: "10px 28px",
                      marginTop: "10px",
                      color: "#00000",
                      width: "47vw",
                      boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
                      borderRadius: 3,
                      inset: "0px 9px 1px -27px",
                      maxHeight: "260px",
                      overflow: "auto",
                      ...props.style,
                    }}
                  >
                    {loading && <span>Loading Data...</span>}
                    {!suggestions?.length && !loading && <span>No matching details found...</span>}
                    {!!suggestions?.length && !loading && <table style={{ borderCollapse: "collapse", width: "100%" }}>
                      <thead className="border-bottom">
                        <tr>
                          <th className="tableContent">Name</th>
                          <th className="tableContent">Phone</th>
                          <th className="tableContent">Email</th>
                        </tr>
                      </thead>
                      <tbody>
                        {suggestions?.map((item: any) => (
                          <tr className="tableRow" key={item?.id} onClick={() => handleRowClick(item?.id)}>
                            <td className="tableContent py-2">{item.firstName + " " + item.lastName}</td>
                            <td className="tableContent py-2">{item.phoneNumber}</td>
                            <td className="tableContent py-2">{item.email}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>}
                  </div>
                )}
              </Overlay>
            )}
            <Row className="mt-5 mb-2" key={resetForm}>
              <p style={{ fontSize: "22px", fontWeight: "700", lineHeight: "28.64px", color: "#3A3A3A" }} className="m-0">
                {!selectedUser?.id ? "Add New Customer Details" : "Existing Customer Details"}
              </p>
              <CustomerForm setStepNumber={setStepNumber} setCustomerEnquiryData={setCustomerEnquiryData} />
            </Row>
          </div>
          {!isEmpty(customerEnquirydata) && <CustomerEnquiry setCustomerEnquiryData={setCustomerEnquiryData} customerEnquirydata={customerEnquirydata} setStepNumber={setStepNumber}/>}
          {!!prevQuotationData?.length && (
            <>
              <div className="py-4">
                <h3>Search Result for - {selectedUser?.firstName} {selectedUser?.lastName}</h3>
              </div>
              <div>
                <p className="text-[#17A300] " style={{ fontSize: "17px", fontWeight: "500" }}>
                  Active
                </p>
                {prevQuotationData?.map((item) => {
                  return <CustomerQuotationRow item={item} fetchData={()=>handleRowClick(selectedUser?.id)} />;
                })}
              </div>
            </>
          )}
        </Col>
      </Row>
    </div>
  );
};

export default BasicDetails;
